import React from 'react';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { Modal } from '../../common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';

interface FieldError {
  key: string;
  label: string;
  message: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  fieldErrors: FieldError[];
  onItemClick: (key: string) => void;
}

export const ErrorSummaryModal: React.FC<Props> = ({ isOpen, onClose, fieldErrors, onItemClick }) => {
  const { t } = useLanguage();

  return (
    <Modal
      isOpen={isOpen && fieldErrors.length > 0}
      onClose={onClose}
      title={
        fieldErrors.length === 1
          ? t('inventory.modal.errorSummaryTitleOne')
          : t('inventory.modal.errorSummaryTitle', { count: fieldErrors.length })
      }
      maxWidth="md"
      zIndexClass="z-[60]"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-lg border border-red-100 bg-red-50/60 px-3.5 py-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
          <p className="text-sm text-red-900">
            {fieldErrors.length === 1
              ? "This field is required."
              : "The following fields are required."}
          </p>
        </div>
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
          {fieldErrors.map(({ key, label, message }) => (
            <li key={key}>
              <button
                type="button"
                onClick={() => onItemClick(key)}
                className="group flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-red-50/50 focus:bg-red-50/50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-300"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 ring-4 ring-red-50/60">
                  <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{label}</p>
                  <p className="mt-0.5 text-xs text-gray-600 break-words">{message}</p>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-red-500" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {t('common.actions.close')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
